import { run } from "@zootools/email-spell-checker";

/** Full `local@host.tld` — required before calling the library `run()`. */
export const EMAIL_COMPLETE_PATTERN = /^\S+@\S+\.\S+$/;

/**
 * User is past `@` with at least two characters in the host — spell-check can run in real time
 * (library still needs a plausible domain; we also retry with `.com` for common typos on rare TLDs).
 */
export const EMAIL_TYPABLE_PATTERN = /^[^\s@]+@[^\s@]{2,}/;

/** Second-level labels that often fail on exotic TLDs (e.g. `gnail.ai`) where `run(x@gnail.com)` works. */
const TYPO_SECOND_LEVEL_FOR_COM_RETRY = new Set([
    "gnail",
    "gmai",
    "gmial",
    "hotnail",
    "hotmai",
    "yahooo",
    "yaho",
]);

function tryLibraryOnCompleteEmail(email: string): string | null {
    if (!EMAIL_COMPLETE_PATTERN.test(email)) {
        return null;
    }
    try {
        const result = run({ email });
        const full = result?.full?.trim();
        if (!full || full.toLowerCase() === email.toLowerCase()) {
            return null;
        }
        return full;
    } catch {
        return null;
    }
}

/**
 * Returns a corrected email when @zootools/email-spell-checker finds a likely typo
 * (e.g. gnail.com → gmail.com). Handles `gnail.ai` by retrying with a `.com` host when the
 * second-level label is a known typo. Returns null when there is no suggestion or it matches input.
 */
export function getEmailSpellSuggestion(raw: string): string | null {
    const email = raw.trim();
    if (!email || !EMAIL_TYPABLE_PATTERN.test(email)) {
        return null;
    }

    const direct = tryLibraryOnCompleteEmail(email);
    if (direct) {
        return direct;
    }

    if (!EMAIL_COMPLETE_PATTERN.test(email)) {
        return null;
    }

    const at = email.indexOf("@");
    const local = email.slice(0, at);
    const host = email.slice(at + 1);
    const hostLower = host.toLowerCase();

    // Library does not flag this; users often omit "mail" (g.com → gmail.com).
    if (hostLower === "g.com") {
        const suggested = `${local}@gmail.com`;
        if (suggested.toLowerCase() !== email.toLowerCase()) {
            return suggested;
        }
    }

    const secondLevel = host.split(".")[0]?.toLowerCase() ?? "";

    if (secondLevel && TYPO_SECOND_LEVEL_FOR_COM_RETRY.has(secondLevel)) {
        const comHost = `${secondLevel}.com`;
        const altEmail = `${local}@${comHost}`;
        const fromCom = tryLibraryOnCompleteEmail(altEmail);
        if (fromCom && fromCom.toLowerCase() !== email.toLowerCase()) {
            return fromCom;
        }
    }

    return null;
}
